/**
 * 日ごとの取込状況からの削除(2026-10-17 発注者指示「この画面でデータを削除」)。違う日付に取り込んだときの取り消し用。
 * 消すもの(OPレポート/対応履歴/両方)を選び、確認のチェックを入れてから削除する。削除と作り直しは importRepo.deleteDayImport。
 */
import { useState } from 'react';
import Modal from '@core/components/Modal';
import { useAuth } from '@core/auth/AuthProvider';
import { useToast } from '@core/components/Toast';
import { weekdayLabel } from '@core/lib/date';
import type { Company } from '@core/lib/types';
import { fetchOperators } from '@core/masters/operatorRepo';
import { formatCount } from '@core/utils/formatters';
import { deleteDayImport, type DayDeleteTarget } from './importRepo';
import type { ImportStatusRow } from './importStatus';

export default function ImportDeleteModal({ row, companies, companyOfCode, onClose, onDeleted }: { row: ImportStatusRow; companies: Company[]; companyOfCode: Map<string, string>; onClose: () => void; onDeleted: () => void }) {
  const { user } = useAuth();
  const toast = useToast();
  const hasOp = !!row.op;
  const hasAct = row.activity.length > 0;
  const options: { key: DayDeleteTarget; label: string; note: string }[] = [
    ...(hasOp ? [{ key: 'op_report' as const, label: 'OPレポートだけ削除', note: `${row.op!.fileName}(${formatCount(row.op!.rowCount)}行)。稼働時間・通話が消え、取得だけ残ります` }] : []),
    ...(hasAct
      ? [{ key: 'activity' as const, label: '対応履歴だけ削除', note: `${row.activity.map((a) => `${a.codes.map((c) => companyOfCode.get(c) ?? c).join('・') || '業務なし'} ${formatCount(a.appointments)}件`).join('、')}。取得が消え、稼働時間は残ります` }]
      : []),
    { key: 'all', label: 'この日のデータをすべて削除', note: 'OPレポート・対応履歴の両方を消し、この日の日次集計も消します' },
  ];
  const [target, setTarget] = useState<DayDeleteTarget>(options[0]!.key);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const dayLabel = `${Number(row.date.slice(5, 7))}/${Number(row.date.slice(8))}${weekdayLabel(row.date)}`;

  async function run() {
    if (!user) return;
    setBusy(true);
    try {
      const operators = await fetchOperators();
      const r = await deleteDayImport({ date: row.date, target, operators, companies, userId: user.id });
      toast(`${dayLabel} の${target === 'op_report' ? 'OPレポート' : target === 'activity' ? '対応履歴' : 'データ'}を削除しました${r.summaryDeleted ? '(日次集計も削除)' : ''}`);
      onDeleted();
    } catch (e) {
      toast(e instanceof Error ? `削除できませんでした: ${e.message}` : '削除できませんでした');
      setBusy(false);
    }
  }

  return (
    <Modal onClose={busy ? () => undefined : onClose} width="min(560px, 94vw)">
      <h3 className="training-modal-title">{dayLabel} のデータを削除</h3>
      <fieldset className="unknown-link-choices" disabled={busy}>
        <legend className="sr-only">削除するもの</legend>
        {options.map((o) => (
          <label key={o.key} className="unknown-link-choice">
            <input type="radio" name="import-delete" checked={target === o.key} onChange={() => setTarget(o.key)} />
            <span>
              <b>{o.label}</b>
              <br />
              <span className="ui-hint">{o.note}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <p className="ui-hint">日次【集計】・PL・本人成績などのこの日の数字が変わります。納品・経費・勤怠の手修正は消えません。正しいファイルは、削除のあとに取り込み直してください。</p>
      <label className="unknown-link-choice">
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} disabled={busy} />
        <span>{dayLabel} のデータを削除することを確認しました</span>
      </label>
      <div className="training-actions">
        <button type="button" className="ui-btn ui-btn-secondary ui-btn-danger" onClick={() => void run()} disabled={!agreed || busy}>
          {busy ? '削除中…' : '削除する'}
        </button>
        <button type="button" className="ui-btn ui-btn-secondary" onClick={onClose} disabled={busy}>
          やめる
        </button>
      </div>
    </Modal>
  );
}
